import { useBasketBuckets } from "hooks";
import { useDiscounts } from "lib/apollo/hooks";
import type { BasketTotalsType } from "lib/apollo/types";
import { useMemo } from "react";
import { useDiscountSpinner } from "../../components/Basket/hooks/useDiscountSpinner";
import { getBasketTotalDiscountedPrice } from "../../components/Basket/utils/getBasketTotalDiscountedPrice";
import { getTotalPrices } from "./utils/getTotalPrices";

export const useBasketTotals = (): BasketTotalsType => {
  const discounts = useDiscounts();
  const { inLimitItems: items } = useBasketBuckets();
  const isDiscountPending = useDiscountSpinner(discounts);

  return useMemo(() => {
    const {
      productMemberTotal,
      productTypicalTotal,
      membershipMemberTotal,
      productGuestTotal
    } = getTotalPrices(items, discounts);

    const basketMemberPrice = productMemberTotal + membershipMemberTotal;
    const basketTypicalPrice = productTypicalTotal + membershipMemberTotal;
    const memberSavings = productTypicalTotal - productMemberTotal || 0;
    const guestSavings = productGuestTotal - productMemberTotal || 0;
    const discountedMembersPrice = getBasketTotalDiscountedPrice(
      basketMemberPrice,
      discounts
    );
    const discountedTypicalPrice = getBasketTotalDiscountedPrice(
      basketTypicalPrice,
      discounts
    );
    const discountedGuestPrice = getBasketTotalDiscountedPrice(
      productGuestTotal,
      discounts
    );
    const discountedSavings =
      discountedTypicalPrice - discountedMembersPrice || 0;
    const showSpinnerCheck = (price) => (isDiscountPending ? -1 : price);

    return {
      totalMembersPrice: showSpinnerCheck(basketMemberPrice),
      totalTypicalPrice: showSpinnerCheck(basketTypicalPrice),
      discountedMembersPrice: showSpinnerCheck(discountedMembersPrice),
      discountedTypicalPrice: showSpinnerCheck(discountedTypicalPrice),
      discountedSavings: showSpinnerCheck(discountedSavings),
      discountedGuestPrice: showSpinnerCheck(discountedGuestPrice),
      memberSavings: showSpinnerCheck(memberSavings),
      guestSavings: showSpinnerCheck(guestSavings),
      productMemberTotal: showSpinnerCheck(productMemberTotal),
      productTypicalTotal: showSpinnerCheck(productTypicalTotal)
    };
  }, [items, discounts, isDiscountPending]);
};
